/**
 * StaffManagementRepository
 * 직원 관리 API 호출 레이어
 */
import { API_BASE_URL } from '../../../config/api';
import { tokenManager } from '../../../infrastructure/api/tokenManager';
import type { Staff, StaffCreate, StaffUpdate } from '../models/StaffManagement';

/**
 * StaffManagementRepository 클래스
 * 직원 관련 API 호출 담당
 */
export class StaffManagementRepository {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * 인증 헤더 생성
   */
  private getAuthHeaders(): HeadersInit {
    // 🔴 2026-10-19 — 종전엔 없는 이름('token')에서 꺼내 로그인 정보를 한 번도 보내지 않았다.
    //    서버가 확인을 안 해서 드러나지 않았는데, 서버에 로그인 확인을 붙이며 같이 고쳤다.
    const token = tokenManager.getAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /** 로그인이 만료됐으면(401) 한 번 갱신해서 다시 부른다 — 맨 fetch 를 쓰는 곳의 공통 규칙(CLAUDE.md). */
  private async authFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const res = await fetch(url, { ...init, headers: this.getAuthHeaders() });
    if (res.status !== 401) return res;
    try {
      await tokenManager.refreshAccessToken();
    } catch {
      return res;
    }
    return fetch(url, { ...init, headers: this.getAuthHeaders() });
  }

  /**
   * 직원 목록 조회
   */
  async getStaffList(): Promise<Staff[]> {
    // localStorage에서 organization_id 가져오기
    const organizationId = localStorage.getItem('organization_id') || '';
    const url = organizationId
      ? `${this.baseUrl}/api/owner/staff?organization_id=${organizationId}`
      : `${this.baseUrl}/api/owner/staff`;

    const response = await this.authFetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('직원 목록을 불러오는데 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 직원 상세 조회
   */
  async getStaffById(staffId: string): Promise<Staff> {
    const response = await this.authFetch(`${this.baseUrl}/api/owner/staff/${staffId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('직원 정보를 불러오는데 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 직원 생성
   */
  async createStaff(staffData: StaffCreate): Promise<Staff> {
    // localStorage에서 organization_id 가져오기
    const organizationId = localStorage.getItem('organization_id') || '';
    const url = organizationId
      ? `${this.baseUrl}/api/owner/staff?organization_id=${organizationId}`
      : `${this.baseUrl}/api/owner/staff`;

    // 빈 문자열 필드는 제외해서 보냄 (백엔드 EmailStr 검증 실패 방지)
    const payload: Record<string, any> = {
      name: staffData.name,
      phone: staffData.phone,
      role: staffData.role,
    };
    if (staffData.email && staffData.email.trim()) payload.email = staffData.email.trim();
    if (staffData.department && staffData.department.trim()) payload.department = staffData.department.trim();

    const response = await this.authFetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '직원 생성에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 직원 정보 수정
   */
  async updateStaff(staffId: string, staffData: StaffUpdate): Promise<Staff> {
    // 빈 문자열 필드는 제외 (백엔드 EmailStr 검증 실패 방지)
    const payload: Record<string, any> = {};
    for (const [key, value] of Object.entries(staffData)) {
      if (value === undefined || value === null) continue;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) continue;
        payload[key] = trimmed;
      } else {
        payload[key] = value;
      }
    }

    const response = await this.authFetch(`${this.baseUrl}/api/owner/staff/${staffId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '직원 정보 수정에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 직원 삭제
   */
  async deleteStaff(staffId: string): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}/api/owner/staff/${staffId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('직원 삭제에 실패했습니다.');
    }
  }

  /**
   * 비밀번호 재설정
   */
  async resetPassword(staffId: string): Promise<void> {
    const response = await this.authFetch(`${this.baseUrl}/api/owner/staff/${staffId}/reset-password`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('비밀번호 재설정에 실패했습니다.');
    }
  }

  /**
   * 계정 활성화/비활성화
   */
  async toggleActive(staffId: string, isActive: boolean): Promise<Staff> {
    const response = await this.authFetch(`${this.baseUrl}/api/owner/staff/${staffId}/toggle-active`, {
      method: 'POST',
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      throw new Error('계정 상태 변경에 실패했습니다.');
    }

    return response.json();
  }
}

/**
 * StaffManagementRepository 싱글톤 인스턴스
 */
export const staffManagementRepository = new StaffManagementRepository();
